<template>
  <div ref="root" class="relative">
    <zeit-button
      v-if="$slots.default"
      :icon="triggerIcon"
      :postIcon="postIcon"
      :fill="fill"
      :color="color"
      :size="size"
      :layout="layout"
      :class="triggerClass"
      @click="showMenu = true"
    >
      <slot />
    </zeit-button>
    <zeit-button
      v-else
      :icon="triggerIcon"
      :postIcon="postIcon"
      :fill="fill"
      :color="color"
      :size="size"
      :layout="layout"
      :class="triggerClass"
      @click="showMenu = true"
    />

    <Transition>
      <div
        v-if="showMenu"
        class="absolute z-[400] flex overflow-hidden rounded-[15px] bg-white text-zinc-900 shadow-md ring-2 ring-white dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-900"
        :class="menuPosition"
        :style="menuMinWidth ? { minWidth: menuMinWidth } : undefined"
      >
        <teleport v-if="showOverlay" to="#card-overlay"><div class="w-screen h-screen" @click="showMenu = false" /></teleport>
        <div class="flex w-full flex-col">
          <button
            v-for="action in actions" :key="action.label"
            type="button"
            :disabled="action.disabled"
            :aria-current="action.selected ? 'true' : undefined"
            @click="runAction(action)"
            :class="[
              action.css,
              action.selected ? 'bg-primary/10 dark:bg-primary/20 hover:bg-primary/10 dark:hover:bg-primary/20' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800',
              action.disabled ? 'cursor-default' : 'cursor-pointer',
              action.description ? 'min-w-[300px] px-4 py-2' : 'py-2 pl-4 pr-8 whitespace-nowrap',
              'flex w-full items-center gap-6 text-left text-sm font-medium',
            ]"
          >
            <span class="min-w-0 flex-1">
              <span class="block whitespace-nowrap">{{ action.label }}</span>
              <span
                v-if="action.description"
                class="mt-0.5 block text-xs font-normal leading-snug opacity-70 whitespace-normal"
              >{{ action.description }}</span>
            </span>
            <ZeitIcon
              name="Check"
              class="h-4 w-4 flex-none"
              :class="action.selected ? 'opacity-100' : 'opacity-0'"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
  .v-enter-active {
    transition: all 0.1s ease-out;
  }

  .v-leave-active {
    transition: all 0.1s ease-out;
  }

  .v-enter-from,
  .v-leave-to {
    transform: scale(.90);
    opacity: 0;
  }
</style>

<script lang="ts">
  import { defineComponent } from 'vue';
  import ZeitButton from '@/components/ui/v2/ZeitButton.vue';
  import ZeitIcon from '@/components/ui/ZeitIcon.vue';

  export class Action {
    label: string;
    action: () => void;
    css: string;
    description: string;
    selected: boolean;
    disabled: boolean;

    constructor(
      label: string,
      action: () => void,
      css: string = '',
      description: string = '',
      selected: boolean = false,
      disabled: boolean = false,
    ) {
      this.label = label;
      this.action = action;
      this.css = css;
      this.description = description;
      this.selected = selected;
      this.disabled = disabled;
    }
  }

  export default defineComponent({
    components: {
      ZeitButton,
      ZeitIcon,
    },
    props: {
      actions: {
        type: Array<Action>,
        required: true,
      },
      icon: {
        type: String,
        default: 'EllipsisHorizontal',
      },
      showIcon: {
        type: Boolean,
        default: true,
      },
      postIcon: {
        type: String,
        default: '',
      },
      fill: {
        type: String,
        default: 'clear',
      },
      color: {
        type: String,
        default: 'zinc',
      },
      size: {
        type: String,
        default: 'sm',
      },
      layout: {
        type: String,
        default: 'rounded',
      },
      showOverlay: {
        type: Boolean,
        default: true,
      },
      menuPosition: {
        type: String,
        default: 'right-0 top-0 origin-top-right',
      },
      menuMinWidth: {
        type: String,
        default: '',
      },
      triggerClass: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        showMenu: false,
      }
    },
    computed: {
      triggerIcon(): string {
        return this.showIcon ? this.icon : '';
      },
    },
    watch: {
      showMenu(isOpen: boolean) {
        if (isOpen) {
          document.addEventListener('pointerdown', this.onDocumentPointerDown);
          document.addEventListener('keydown', this.onDocumentKeydown);
        } else {
          this.removeDocumentListeners();
        }
      },
    },
    beforeUnmount() {
      this.removeDocumentListeners();
    },
    methods: {
      runAction(action: Action) {
        if (action.disabled) return;
        this.showMenu = false;
        action.action();
      },
      onDocumentPointerDown(event: PointerEvent) {
        const root = this.$refs.root as HTMLElement | undefined;
        if (root && event.target instanceof Node && root.contains(event.target)) return;
        this.showMenu = false;
      },
      onDocumentKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') this.showMenu = false;
      },
      removeDocumentListeners() {
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      },
    },
  });
</script>
